import { gsap } from "gsap"
import { CustomEase } from "gsap/CustomEase"
import { CustomWiggle } from "gsap/CustomWiggle"
import { ScrollToPlugin } from "gsap/ScrollToPlugin"
import { Carousels } from '@/scripts/carousels/SlickCarousel'
import settings from "@/scripts/common/settings"
import utilities from "@/scripts/common/utilities"
import {API} from '@/scripts/common/api/api.services'
import {Video} from '@/scripts/video/Video'
import modalTpl from './templates/modal.template'
gsap.registerPlugin(ScrollToPlugin, CustomEase, CustomWiggle)

export const ModalController = (() => {
    class Modal {
        constructor(trigger, options) {
            this.$trigger = trigger
            this.$targetHTML = this.$trigger.dataset.target ? document.querySelector(this.$trigger.dataset.target) : null
            this.$modal = null
            this.$galleryCarousel = null
            this.$galleryCarouselThumb = null
            this.modalType = this.$trigger.dataset.modalType || 'normal'
            this.modalAnimType = this.$targetHTML?.dataset.modalAnimType || 'slidePage'
            this.modalDisplay = this.$targetHTML?.dataset.modalDisplay || ''
            this.galleryGroup = this.$trigger.dataset.modalGroup || ''
            this.galleryGroupTitle = this.$trigger.dataset.modalGroupTitle || ''
            this.modalTitle = this.$trigger.dataset.modalTitle || ''
            this.mainTl = null
            this.galleryItems = []
            this.active = false

            this.options = Object.assign({
                duration: 0.5,
                ease: 'power3.easeInOut'
            },options)

            this.init()
        }

        init() {
            if(this.$targetHTML) this._createModal()
            this.addListeners()
        }

        // Animation
        addUIAnimation() {
            if (this.mainTl) {
                this.mainTl.kill()
                
                this.mainTl = null
            }

            if (!this.$modal) return

            const $modalInner = this.$modal.querySelector('.modal__inner')

            gsap.set([$modalInner, this.$modal], {clearProps: 'all'})
            
            switch(this.modalAnimType) {
                case 'fromTop':
                    this.mainTl = this.fromTopAnim()
                    break
                default:
                    this.mainTl = this.slidePageAnim() 
            }
            

        }

        //Animations
        slidePageAnim() {
            const $modalInner = this.$modal.querySelector('.modal__inner')
            const tl = gsap.timeline({
                paused: true
            })

            gsap.set(this.$modal, {width: 0})

            tl.to(this.$modal, {
                duration: 0.5,
                ease: 'power2.inOut',
                width: '100%'
            })
                .from($modalInner, {
                    autoAlpha: 0,
                    duration: 0.5,
                    ease: 'power2.inOut',
                    xPercent: -100
                }, 0)
            return tl
        }
        // From Top
        fromTopAnim() {
            const $modalContainer = this.$modal.querySelector('.modal__container')
            const tl = gsap.timeline({
                paused: true
            })
            
            gsap.set(this.$modal, {
                autoAlpha: 0,
                display: 'none'
            })

            gsap.set($modalContainer, {
                autoAlpha: 0,
                yPercent: -100,
                autoAlpha: 0
            })

            tl.to(this.$modal, {
                duration: this.options.duration,
                ease: this.options.ease,
                autoAlpha: 1,
                display: 'block'
            })
            .to($modalContainer, {
                duration: this.options.duration,
                ease: this.options.ease,
                yPercent: 0,
                autoAlpha: 1 
            }, '-=0.2')

            return tl
        }
        // From side
        fromSideAnim() {
            const $modalContainer = this.$modal.querySelector('.modal__container')
            const tl = gsap.timeline({
                paused: true
            })
            
            gsap.set(this.$modal, {
                autoAlpha: 0,
                display: 'none'
            })

            tl.to(this.$modal, {
                duration: this.options.duration,
                ease: this.options.ease,
                autoAlpha: 1,
                display: 'block'
            })
            .from($modalContainer, {
                duration: this.options.duration,
                ease: this.options.ease,
                xPercent: this.options.modalDirection === 'right' ? 100 : -100,
            }, '-=0.2')

            return tl
        }

        // Shake 
        shakeAnim() {
            const $modalContent = this.$modal.querySelector('.modal__content')
            if(!$modalContent) return

            CustomWiggle.create("Wiggle.easeInOut", {wiggles:this.options.wiggles, type:"easeInOut"});
            CustomWiggle.create("Wiggle.easeOut", {wiggles:this.options.wiggles, type:"easeOut"});
            gsap.to($modalContent, {
                duration: 0.8,
                x:"+=20", 
                ease: "Wiggle.easeOut"
            })
        }

        // Modal

        _clearModal() {
            if (this.$galleryCarousel) {
                this.$galleryCarousel.getSlick().unslick()
                this.$galleryCarousel = null
            }
            if (this.$galleryCarouselThumb) {
                this.$galleryCarouselThumb.getSlick().unslick()
                this.$galleryCarouselThumb = null
            }
            if (this.$modal) {
                this.$modal.remove()
                this.$modal = null
            }
        }

        _createModal() {
            const existingModal = this.$targetHTML && document.querySelector(`#${this.$targetHTML.getAttribute('id')}--modal`)
            if(existingModal) {
                this.$modal = existingModal
                this.calculateGrid()
                this.addUIAnimation()
                this.addModalActions()
                return
            }
            if (this.$modal && this.modalType === 'gallery') {
                this._clearModal()
            }
            this.$modal = utilities.createNodeParsing(modalTpl())
            if(this.modalDisplay === 'modal-normal') {
                const $backdrop = document.createElement('div')
                $backdrop.classList.add('modal__backdrop')
                this.$modal.prepend($backdrop)
                this.$modal.classList.add('modal--normal')
            }

            if (this.modalType === 'gallery') {
                this.collectGallery()
                this._createModalHeader()
                this._createGalleryCarousel()
                this._createGalleryThumbnailCarousel()
                document.body.appendChild(this.$modal)
                this.carousels()
            } else {
                this._createModalHeader()
                this._createModalFooter()
                this._createModalBody()
                this.$modal.setAttribute('id', `${this.$targetHTML.getAttribute('id')}--modal`)
                document.body.appendChild(this.$modal)
            }
            this.calculateGrid()
            this.addUIAnimation()
            this.addModalActions()

        }

        // Modal elems
        _createModalHeader() {
            const $modalHeader = this.$modal.querySelector('.modal__header')
            const $targetHeader = this.$targetHTML?.querySelector('[data-title]')
            if($targetHeader) {
                $modalHeader.appendChild($targetHeader)
                return
            }

            if(this.modalTitle) {
                
                const $title = document.createElement('h1')
                $title.innerHTML = `${this.modalTitle}`
                $modalHeader.appendChild($title)
            }

            if(this.galleryGroupTitle) {
                const $grpTitle = document.createElement('h4')
                $grpTitle.innerHTML = `${this.galleryGroupTitle}`
                $modalHeader.appendChild($grpTitle)
            } 
            const headerModifiers = this.$targetHTML?.dataset.headerModifiers?.split(',').map(val => val.trim()) || []
            if(headerModifiers.length)
                headerModifiers.forEach((mod) => $modalHeader.classList.add(`${mod}`))

            if(this.modalType === 'gallery') $modalHeader.classList.add(`justify-content-center`)
            
            return
        }

        _createModalFooter() {
            const $modalFooter = this.$modal.querySelector('.modal__footer')
            const $targetFooter = this.$targetHTML?.querySelector('[data-footer]')
            const footerModifiers = this.$targetHTML?.dataset.footerModifiers?.split(',').map(val => val.trim()) || []
            if(footerModifiers.length)
                footerModifiers.forEach((mod) => $modalFooter.classList.add(`${mod}`))
            if($targetFooter) {
                $modalFooter.appendChild($targetFooter)
                return
            }
        }

        _createModalBody() {
            const $modalBody = this.$modal.querySelector('.modal__body')
            const $modalContainer = this.$modal.querySelector('.modal__container')
            this.$targetHTML?.classList.add('modal-added')
            const bodyModifiers = this.$targetHTML?.dataset.bodyModifiers?.split(',').map(val => val.trim()) || []
            const modifiers = this.$targetHTML?.dataset.modifiers?.split(',').map(val => val.trim()) || []
            if(bodyModifiers.length)
                bodyModifiers.forEach((mod) => $modalBody.classList.add(`${mod}`))
            if(modifiers.length)
                modifiers.forEach((mod) => $modalContainer.classList.add(`${mod}`))
            $modalBody.appendChild(this.$targetHTML)
        }

        // Gallery
        _createGalleryCarousel() {
            this.$galleryCarousel = document.createElement('div')
            this.$galleryCarousel.classList.add('modal-single-carousel', 'card-carousel--overlay-controls', 'card-carousel--arrows-btn')
            this.galleryItems.forEach((item) => {
                const $singleSlide = document.createElement('div')
                $singleSlide.classList.add('modal-single-carousel__slide', 'modal-single-carousel__item')
                if(!item.iframeSrc) {
                    $singleSlide.innerHTML = `<div class="bg-img"><img class="modal-single-carousel__img" data-lazy='${item.imageLink}' /></div>`
                    this.$galleryCarousel.appendChild($singleSlide)
                    return
                }

                $singleSlide.appendChild(this._createIframe(item)) 
                this.$galleryCarousel.appendChild($singleSlide)
                
            })
            const $modalBody = this.$modal.querySelector('.modal__body')
            $modalBody.appendChild(this.$galleryCarousel)
            
        }

        _createIframe(item) {
            const banner = document.createElement('div')
            banner.classList.add("banner","contain-bg", "banner-video", "text-light", "text-center", "aspect-ratio--16x9")
            if(item.iframeType !== 'html5') banner.dataset.videoType = item.iframeType
            banner.innerHTML = `<div class="background">
                <div class="bg-img"><img class="modal-single-carousel__img " data-lazy='${item.imageLink}' /></div>
                ${item.iframeType === 'html5' ? 
                    `<video class="bg-vid bg-vid--parallax-transform" data-object-fit="cover"><source type="video/mp4" src="${item.iframeSrc}"></video>
                     <div class="bg-vid__controls" aria-hidden="true"><span class="bg-vid__controls-play"><svg class="icon-svg icon-svg--outline icon-svg--md"><use xlink:href="#icon-play"></use></svg></span></div>` 
                     :
                    `<div class="bg-vid__player" data-video-id="${item.iframeSrc}"></div>` +
                    `<div class="bg-vid__controls" aria-hidden="true"><span class="bg-vid__controls-play"><svg class="icon-svg icon-svg--outline icon-svg--md"><use xlink:href="#icon-play"></use></svg></span></div>` 
                }
            </div>`
            new Video.VideoBanner(banner, {
                fitIframe: false
            })
            return banner
        }

        _createGalleryThumbnailCarousel() {
            if (this.galleryItems.length === 0) return

            this.$galleryCarouselThumb = document.createElement('div')
            this.$galleryCarouselThumb.classList.add('modal-thumbnail-carousel', 'card-carousel--inline-controls', 'card-carousel--arrows-btn')

            this.galleryItems.forEach((item) => {
                const $thumbSlide = document.createElement('div')
                $thumbSlide.classList.add('modal-thumbnail-carousel__slide', 'modal-thumbnail-carousel__item')
                $thumbSlide.innerHTML = `<div class="bg-img"><img class="modal-thumbnail-carousel__img" data-lazy='${item.thumbnailLink}' /></div>`
                this.$galleryCarouselThumb.appendChild($thumbSlide)
            })
            const $modalFooter = this.$modal.querySelector('.modal__footer')
            $modalFooter.classList.add('modal__footer--thumbnail-wrapper')
            $modalFooter.appendChild(this.$galleryCarouselThumb)
        }

        // Gallery
        collectGallery() {
            this.galleryItems = []
            const $relatedGroupItems = this.galleryGroup ? 
            (!new API.Controller().isJson(this.$trigger.dataset.modalGroup) ? document.querySelectorAll(`[data-modal-group=${this.galleryGroup}]`) : JSON.parse(this.$trigger.dataset.modalGroup))
            : [this.$trigger]

            $relatedGroupItems.forEach($item => {
                let isItemNode = utilities.isNodeEle($item)
                const imageLink = isItemNode ? $item.getAttribute('href') : $item.imageLink
                const iframeSrc = isItemNode ? $item.dataset.iframeSrc || null : $item.iframeSrc || null
                const iframeType = isItemNode ? $item.dataset.iframeType || null : $item.iframeType || null
                const thumbnailLink = isItemNode ? $item.dataset.thumbnail || imageLink : $item.thumbnailLink
                this.pushToGallery({
                    imageLink,
                    thumbnailLink,
                    iframeSrc,
                    iframeType
                })
            })
        }

        pushToGallery(item) {
            if (this.galleryItems.findIndex(i => i.imageLink === item.imageLink && !i.iframeSrc) === -1) {
                this.galleryItems.push(item)
            }
        }

        // Carousels
        carousels() {
            const initIndex = this.galleryItems.findIndex(i => i.imageLink === this.$trigger.getAttribute('href')) !== -1 ? this.galleryItems.findIndex(i => i.imageLink === this.$trigger.getAttribute('href')) : 0
            // Single carousel
            new Carousels.SlickCarousel(this.$galleryCarousel, {
                asNavFor: '.modal-thumbnail-carousel',
                lazyLoad: 'ondemand',
                infinite: false,
                initialSlide: initIndex,
                slidesToShow: 1,
                slidesToScroll: 1,
                focusOnSelect: false,
                dots: false
            }).init()

            // Thumbnail carousel
            new Carousels.SlickCarousel(this.$galleryCarouselThumb, {
                asNavFor: '.modal-single-carousel',
                lazyLoad: 'ondemand',
                //variableWidth: true,
                infinite: false,
                slidesToShow: 8,
                slideToScroll: 1,
                focusOnSelect: true,

                responsive: [
                    {
                        breakpoint: 1025,
                        settings: {
                            centerMode: false,
                            slidesToShow: 7,
                            infinite: false
                        }
                    },
                    {
                        breakpoint: 768,
                        settings: {
                            centerMode: false,
                            slidesToShow: 4,
                            infinite: false
                        }
                    },
                    {
                        breakpoint: 568,
                        settings: {
                            centerMode: false,
                            slidesToShow: 3,
                            infinite: false,
                            centerPadding: '30px'
                        }
                    }
                ],
                dots: false
            }).init()

            const $slides = this.$galleryCarouselThumb.querySelectorAll('.modal-thumbnail-carousel__slide')

            $slides[initIndex].click() // For active slick thumbnail sync issue

            const $focusEle = this.$galleryCarousel.querySelector('.modal-single-carousel__slide')

            utilities.triggerFocus($focusEle)
        }

        // Grid calculations
        calculateGrid() {
            if (!this.$modal) return
            const $modalAction = this.$modal.querySelector('.modal__action')
            const $modalHeader = this.$modal.querySelector('.modal__header')
            const $modalContainer = this.$modal.querySelector('.modal__container')
            const $modalFooter = this.$modal.querySelector('.modal__footer')
            const $modalCarousel = this.$modal.querySelector('.modal-single-carousel')

            if (this.modalType == 'gallery') {
                const carouselHeight = window.innerHeight - ($modalAction.getBoundingClientRect().height +
                    $modalHeader.getBoundingClientRect().height +
                    $modalFooter.getBoundingClientRect().height
                )
                $modalCarousel.style.height = `${carouselHeight}px`
            }

            //this.$modal.style.width = `${window.innerWidth}px`
        }
        
        // Modal actions
        modalOpen() {
            if(!this.$modal) return 
            this.active = true
            this.updateHtml()
            utilities.toggleWindowScroll(false)
            this.mainTl.play()
            this.mainTl.eventCallback('onComplete', () => {
                this.$modal.classList.remove('modal--tweening')
            })
        }

        modalClose() {
            if(!this.$modal) return
            this.active = false
            this.$modal.classList.add('modal--tweening')
            this.mainTl.reverse()
            this.mainTl.eventCallback('onReverseComplete', () => {
                this.updateHtml()
                utilities.toggleWindowScroll(true)
                if(this.modalType === 'gallery') this._clearModal()
            })
        }

        updateHtml() {
            if (this.active) {
                this.$modal.classList.add('modal--tweening')
                document.body.classList.add('modal-open')
                return
            }

            this.$modal.classList.remove('modal--tweening')
            document.body.classList.remove('modal-open')
        }

        addModalActions() {
            const $closeModal = this.$modal.querySelector('.modal__action-close')
            $closeModal.addEventListener('click', () => this.modalClose())
        }


        // Event Handlers
        handleTriggerClick(e) {
            e.preventDefault()
            if(this.modalType === 'gallery') this._createModal()
            this.modalOpen()
        }

        // Resize Func
        resizeFunc() {
            return utilities.debounce(() => {
                this.calculateGrid()
                //this.addUIAnimation() // ui animation rebuild causes it to close
            }, 250)
        }

        addListeners() {
            window.addEventListener('resize', this.resizeFunc())
            this.$trigger.addEventListener('click', (e) => this.handleTriggerClick(e))

            const $backdrop = this.$modal?.querySelector('.modal__backdrop')
            $backdrop?.addEventListener("click", () => this.active && this.modalClose())
        }
    }

    const CLASS_MODAL_INLINE_PRE = "modal-inline"

    class ModalInline {
        constructor(container) {
            this.$modal = container
            this.$modalItems = this.$modal.querySelectorAll(`.${CLASS_MODAL_INLINE_PRE}__item:not([style*="display: none"])`)
            this.$modalPanels = this.$modal.querySelectorAll(`.${CLASS_MODAL_INLINE_PRE}__item:not([style*="display: none"]) > .${CLASS_MODAL_INLINE_PRE}__panel`)
            this.totalItemsPerRow = 3
            this.totalItems = this.$modalItems.length
            this.init()
        }

        init() {
            this.updateTotalItemsPerRow()
            this.updateItems()
            this.placeModalPanels()
            this._addEventListener()
        }

        resetAllPanels() {
            this.$modalItems.forEach(($item, i) => {
                if(this.$modalPanels[i]) $item.appendChild(this.$modalPanels[i])
            })
        }

        updateTotalItemsPerRow() {
            this.totalItemsPerRow = 1
            if(window.innerWidth > 575) this.totalItemsPerRow = 2 
            if(window.innerWidth > 1024) this.totalItemsPerRow = 3 
        }

        updateItems() {
           this.$modalItems = this.$modal.querySelectorAll(`.${CLASS_MODAL_INLINE_PRE}__item:not([style*="display: none"])`)
           this.$modalPanels = this.$modal.querySelectorAll(`.${CLASS_MODAL_INLINE_PRE}__item:not([style*="display: none"]) > .${CLASS_MODAL_INLINE_PRE}__panel`)
           this.totalItems = this.$modalItems.length
        }

        placeModalPanels() {
            let itemsPerRowOffset = this.totalItemsPerRow
            this.$modalItems.forEach(($item, i) => {
                const $panel = this.$modalPanels[i]
                if(i === itemsPerRowOffset) itemsPerRowOffset += this.totalItemsPerRow
                if (i <= itemsPerRowOffset - 1) {
                    if ($panel) {
                        (this.$modalItems[itemsPerRowOffset - 1]) ? 
                            $panel.appendAfter(this.$modalItems[itemsPerRowOffset - 1]) : 
                            $panel.appendAfter(this.$modalItems[this.totalItems - 1])
                    } 
                    return
                } 
                
            })
        }

        // CAllbacks
        onPanelUpdate(e) {
            const $item = e.target.closest(`.${CLASS_MODAL_INLINE_PRE}__item`)
            e.detail.isActive() ? $item.classList.add(`${CLASS_MODAL_INLINE_PRE}__item--active`) : $item.classList.remove(`${CLASS_MODAL_INLINE_PRE}__item--active`)


        }

        onFilter() {
            this.resetAllPanels()
            this.updateItems()
            this.placeModalPanels()
        }

        // Resize func
        resizeFunc() {
            return utilities.debounce(() => {
                this.updateTotalItemsPerRow()
                this.placeModalPanels()
            }, 250)
        }

        _addEventListener() {
            this.$modalItems.forEach($item => {
                $item.addEventListener('panelOpen', (e) => this.onPanelUpdate(e))
                $item.addEventListener('panelClose', (e) => this.onPanelUpdate(e))
            })

            this.$modal.addEventListener('UIFilterUpdated', (e) => this.onFilter(e))

            window.addEventListener('resize', this.resizeFunc())
        }
    }



    return {
        Modal,
        ModalInline
    }
})()